import { useState, useEffect, useRef } from 'react';
import AnalyticsVisualization from '../../framework/analyticsVisualization';
import { dashboardUrl } from '../../../service/config';
import useStyles from '../../jobs/jobs_styles';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import { FormControlLabel, Button, Stack } from '@mui/material';
import { ReactComponent as FilterIcon } from '../../../assets/ml_summary_filter.svg';
import { useSearchParams } from 'react-router-dom';
import { getDecodedToken, getSector } from '../../../service/service';
import { useTheme } from "@mui/material/styles";

interface ModelSummaryProps {
	sharedValue: any;
	updateSharedValue: (value: any) => void;
}
const ModelSummary: React.FC<ModelSummaryProps> = ({ sharedValue, updateSharedValue }) => {
	const theme= useTheme();
	const colors = theme.palette;
	const userSector = getDecodedToken()?.sector?.name?.toLowerCase();
	const classes = useStyles();
	const dataFetchedRef = useRef(false);
	const [sectorOptionsList, setSectorOptionsList] = useState<any[]>([]);
	const [filterMenu, setFilterMenu] = useState<{
		mouseX: number;
		mouseY: number;
	} | null>(null);
	const [itemsChecked, setItemsChecked] = useState(false);
	const [checkedList, setCheckedList] = useState<any[]>([]);
	const [currSectorFilter, setCurrSectorFilter] = useState<any[]>([]);

	const [searchParams] = useSearchParams();
	const hasDrillDown = searchParams.has('isDrillDowned');

	const filterClick = (event: any) => {
		event.preventDefault();
		setFilterMenu(
			filterMenu === null
				? {
						mouseX: Number.parseInt(event.clientX) - 20,
						mouseY: Number.parseInt(event.clientY) - 6
				  }
				: null
		);
	};
	const handleClose = (event: any) => {
		setFilterMenu(null);
	};
	const selectAllCheck = (e: any) => {
		const { checked } = e.target;
		const collection: any[] = [];
		if (checked) setCheckedList(sectorOptionsList);
		else setCheckedList([]);
		setItemsChecked(checked);
	};
	const handleChange = (e: any) => {
		const { value, checked } = e.target;
		const newlyChecked = [];
		if (checked && e.target.value) {
			newlyChecked.push(e.target.value);
		}
		if (checked) {
			setCheckedList([...checkedList, ...newlyChecked]);
		} else {
			setCheckedList(checkedList.filter((item) => item !== value));
		}
	};

	const handleOkClick = (event: any) => {
		// Create a new list with "S&T" replaced by "SNT"
		const updatedCheckedList = checkedList.map((value: string) => {
			if (value.includes('S&T')) {
				return value.replace('S&T', 'SNT');
			}
			return value;
		});
	
		if (updatedCheckedList.length > 0) {
			setCurrSectorFilter(updatedCheckedList);
		} else {
			setCurrSectorFilter([]);
		}
		updateSharedValue({ sectorFilter: updatedCheckedList });
		console.log(updatedCheckedList);
		localStorage.setItem('ml-summary-sector-filter', JSON.stringify(updatedCheckedList));
		handleClose(event);
	};
	async function fetchSectorOptionsData() {
		const sectorRes = await getSector('1', '100', '');
		if (sectorRes.success) {
			setSectorOptionsList(sectorRes.data.sector.map((x: any) => x.name));
		}
	}

	useEffect(() => {
		if (dataFetchedRef.current) return;
		dataFetchedRef.current = true;

		if (userSector === 'all') fetchSectorOptionsData();
		let storedSectorFilter = localStorage.getItem('ml-summary-sector-filter')
			? JSON.parse(localStorage.getItem('ml-summary-sector-filter') ?? '')
			: null;
		if (storedSectorFilter) {
			setCheckedList(storedSectorFilter);
			setCurrSectorFilter(storedSectorFilter);
		}
	});
	return (
		<Stack>
			{userSector === 'all' ? (
				<FilterIcon onClick={filterClick} 
					style={{
						position: 'fixed',
						top: '82px',
						left: '95%',
						cursor: 'pointer',
						zIndex:999
					}} 
				/>
			) : (
				<div className="sector-filter-text-ml">Sector: {getDecodedToken()?.sector?.name}</div>
			)}

			<Popover
				open={filterMenu !== null}
				onClose={handleClose}
				className="filterContainer"
				anchorReference="anchorPosition"
				anchorPosition={filterMenu === null ? undefined : { top: filterMenu.mouseY, left: filterMenu.mouseX }}
			>
				<div className="text-filter-row">
					<Stack sx={{
						maxHeight: 200,
              			overflowY: 'auto',
						backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
					}}>
						<MenuItem className="selectAll"
						sx={{
							backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
							'&:hover': {
							backgroundColor: `${colors.dropdown.menuItemHover} !important`,
							'& .MuiFormControlLabel-label': {
								color: `${colors.dropdown.menuItemHoverText} !important`,
							},
							},
						}}
						>
							<FormControlLabel
								className={classes.checkbox}
								label="All"
								sx={{
									'& .MuiFormControlLabel-label': {
										color: `${colors.dropdown.menuItemText} !important`,
									},
								}}
								control={
									<Checkbox
										color="success"
										value="all"
										checked={itemsChecked}
										onChange={(e) => {
											selectAllCheck(e);
										}}
										sx={{
											'& .MuiSvgIcon-root': {
												color: `${colors.dropdown.checkBox} !important`,
											},
										}}
									/>
								}
							/>
						</MenuItem>
						<div className="checkbox-sec">
							{sectorOptionsList?.map((filter: any) => {
								let labelText = filter;
								if (filter === true) {
									labelText = 'Yes';
								} else if (filter === false) {
									labelText = 'No';
								}

								return (
									<MenuItem key={filter}
									sx={{
										backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
										'&:hover': {
											backgroundColor: `${colors.dropdown.menuItemHover} !important`,
										'& .MuiFormControlLabel-label': {
											color: `${colors.dropdown.menuItemHoverText} !important`,
										},
										},
									}}>
										<FormControlLabel
											className={classes.checkbox}
											sx={{
												'& .MuiFormControlLabel-label': {
													color: `${colors.dropdown.menuItemText} !important`,
												},
											}}
											label={labelText}
											control={
												<Checkbox
													color="success"
													value={filter}
													checked={
														(filter === true && checkedList.includes('true')) ||
														(filter === false && checkedList.includes('false')) ||
														checkedList.includes(filter)
													}
													onChange={(e) => {
														handleChange(e);
													}}
													sx={{
														'& .MuiSvgIcon-root': {
															color: `${colors.dropdown.checkBox} !important`,
														},
													}}
												/>
											}
										/>
									</MenuItem>
								);
							})}
						</div>
					</Stack>
					<div className="buttonSec">
						<Button variant="contained" className={classes.cancelBtn} onClick={handleClose}>
							cancel
						</Button>
						<Button variant="contained" className={classes.okBtn} onClick={handleOkClick}>
							ok
						</Button>
					</div>
				</div>
			</Popover>
			<AnalyticsVisualization
				params={{
					dashboardUrl: dashboardUrl.mlobsSummary,
					className: 'mlobsSummaryIframeContainer',
					otherParams: {
						from: 'now-6h',
						to: 'now',
						'var-sectors': currSectorFilter,
						'var-drillDownStatus': hasDrillDown ? searchParams.get('status') : ''
					},
					seamless: true
				}}
			/>
		</Stack>
	);
};

export default ModelSummary;
